/*
 * Copyright (c) 2024, King Living
 * @override KINDW-1531 — preserve scroll on reload while Contentful modules hydrate; scroll top on SPA nav only.
 */

import {useEffect, useLayoutEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

const STORAGE_KEY = 'kl:scroll-restore';

function routeKey(pathname, search) {
  return `${pathname || '/'}${search || ''}`;
}

function readSavedScrollY(key) {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY));

    return parsed?.key === key && typeof parsed.scrollY === 'number' ? parsed.scrollY : null;
  } catch {
    return null;
  }
}

function restoreWhenReady(targetY) {
  let lastHeight = 0;
  let stable = 0;

  const apply = () => {
    window.scrollTo(0, Math.max(0, targetY));
    requestAnimationFrame(() => window.scrollTo(0, Math.max(0, targetY)));
  };

  const tick = () => {
    const height = document.documentElement.scrollHeight;
    const maxScroll = Math.max(0, height - window.innerHeight);

    if (height === lastHeight) {
      stable += 1;
    } else {
      lastHeight = height;
      stable = 0;
    }

    if (maxScroll >= targetY - 50 && stable >= 3) {
      apply();
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    }
  };

  tick();
  const intervalId = setInterval(tick, 100);
  const timeoutId = setTimeout(apply, 8000);

  return () => {
    clearInterval(intervalId);
    clearTimeout(timeoutId);
  };
}

export default function ScrollToTop() {
  const {pathname, search} = useLocation();
  const isFirstPathnameEffect = useRef(true);

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !('scrollRestoration' in window.history)) {
      return undefined;
    }

    const previous = window.history.scrollRestoration;

    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const key = routeKey(pathname, search);

    const save = () => {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({key, scrollY: window.scrollY}));
      } catch {
        // Ignore
      }
    };

    window.addEventListener('pagehide', save);

    return () => window.removeEventListener('pagehide', save);
  }, [pathname, search]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const navType = performance.getEntriesByType('navigation')[0]?.type;

    if (navType !== 'reload' && navType !== 'back_forward') {
      return undefined;
    }

    const targetY = readSavedScrollY(routeKey(pathname, search));

    if (targetY == null || targetY <= 0) {
      return undefined;
    }

    return restoreWhenReady(targetY);
  }, [pathname, search]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isFirstPathnameEffect.current) {
      isFirstPathnameEffect.current = false;

      return;
    }

    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
